import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { 
  ArrowLeft,
  RefreshCw,
  AlertTriangle,
  Shield,
  Clock,
  CheckCircle,
  XCircle,
  EyeOff,
  ExternalLink,
  Copy,
  Filter
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { RiskScoreCard } from '@/components/RiskScoreCard'
import { ExportMenu } from '@/components/ExportMenu'
import { cn } from '@/utils/cn'

import { useScanStore } from '@/stores/scanStore'
import { useUIStore } from '@/stores/uiStore'
import { useWebSocket } from '@/hooks/useWebSocket'
import { 
  formatDate, 
  formatRelativeTime, 
  formatRepositoryName,
  formatDuration,
  getSeverityBadgeColor
} from '@/utils/formatters'
import scanService from '@/services/scans'
import type { Finding, SeverityLevel, FailedScanner } from '@/types/scan'

const severityOrder: Record<SeverityLevel, number> = {
  CRITICAL: 5,
  HIGH: 4,
  MEDIUM: 3,
  LOW: 2,
  INFO: 1,
}

export function ScanResultsPage() {
  const { scanId } = useParams<{ scanId: string }>()
  const { currentScan, setCurrentScan, scanProgress } = useScanStore()
  const { addToast } = useUIStore()
  const { subscribe, unsubscribe } = useWebSocket()
  
  const [isLoading, setIsLoading] = useState(false)
  const [findings, setFindings] = useState<Finding[]>([])
  const [filteredFindings, setFilteredFindings] = useState<Finding[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedSeverity, setSelectedSeverity] = useState<SeverityLevel | 'ALL'>('ALL')
  const [selectedScanner, setSelectedScanner] = useState<string>('ALL')
  const [dismissedFindings, setDismissedFindings] = useState<Set<string>>(new Set())
  const [showDismissed, setShowDismissed] = useState(false)

  const progress = scanId ? scanProgress[scanId] : null

  useEffect(() => {
    if (!scanId) return

    const loadScanDetails = async () => {
      setIsLoading(true)
      try {
        const [scan, scanFindings] = await Promise.all([
          scanService.getScan(scanId),
          scanService.getFindings(scanId).catch(() => [])
        ])
        
        setCurrentScan(scan)
        setFindings(scanFindings)
        setFilteredFindings(scanFindings)

        // Subscribe to real-time updates for this scan
        subscribe(scanId)
      } catch (error) {
        console.error('Failed to load scan:', error)
        addToast({
          title: 'Failed to Load Scan',
          description: 'Could not retrieve scan details. Please try again.',
          type: 'error',
        })
      } finally {
        setIsLoading(false)
      }
    }

    loadScanDetails()

    // Cleanup: unsubscribe when component unmounts
    return () => {
      if (scanId) {
        unsubscribe(scanId)
      }
    }
  }, [scanId, setCurrentScan, subscribe, unsubscribe, addToast])

  // Filter findings based on search term, severity, scanner, and dismissal status
  useEffect(() => {
    let filtered = findings

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(finding => 
        finding.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        finding.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        finding.file_path?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        finding.package_name?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    // Apply severity filter
    if (selectedSeverity !== 'ALL') {
      filtered = filtered.filter(finding => finding.severity === selectedSeverity)
    }

    // Apply scanner filter
    if (selectedScanner !== 'ALL') {
      filtered = filtered.filter(finding => finding.scanner === selectedScanner)
    }

    // Apply dismissal filter
    if (!showDismissed) {
      filtered = filtered.filter(finding => !dismissedFindings.has(finding.id))
    }

    // Sort by severity (highest first)
    filtered.sort((a, b) => severityOrder[b.severity] - severityOrder[a.severity])

    setFilteredFindings(filtered)
  }, [findings, searchTerm, selectedSeverity, selectedScanner, dismissedFindings, showDismissed])

  const handleDismissFinding = async (findingId: string) => {
    if (!scanId) return

    try {
      await scanService.dismissFinding(scanId, findingId, 'User dismissed')
      setDismissedFindings(prev => new Set(prev).add(findingId))
      addToast({
        title: 'Finding Dismissed',
        description: 'The finding has been dismissed.',
        type: 'success',
      })
    } catch (error) {
      addToast({
        title: 'Dismiss Failed',
        description: 'Could not dismiss the finding. Please try again.',
        type: 'error',
      })
    }
  }

  const handleExportStart = () => {
    addToast({
      title: 'Export Started',
      description: 'Preparing your export...',
      type: 'info',
    })
  }

  const handleExportComplete = () => {
    // Export complete toast is handled by ExportMenu component
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    addToast({
      title: 'Copied',
      description: 'Text copied to clipboard.',
      type: 'success',
      duration: 2000,
    })
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-600" />
      case 'completed_with_warnings':
        return <AlertTriangle className="h-5 w-5 text-yellow-600" />
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-600" />
      case 'running':
        return <RefreshCw className="h-5 w-5 animate-spin text-blue-600" />
      case 'pending':
        return <Clock className="h-5 w-5 text-yellow-600" />
      default:
        return <Shield className="h-5 w-5 text-gray-400" />
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed_with_warnings':
        return 'Completed with Warnings'
      default:
        return status.charAt(0).toUpperCase() + status.slice(1)
    }
  }

  const uniqueScanners = Array.from(new Set(findings.map(f => f.scanner)))
  const severityCount = {
    CRITICAL: findings.filter(f => f.severity === 'CRITICAL').length,
    HIGH: findings.filter(f => f.severity === 'HIGH').length,
    MEDIUM: findings.filter(f => f.severity === 'MEDIUM').length,
    LOW: findings.filter(f => f.severity === 'LOW').length,
    INFO: findings.filter(f => f.severity === 'INFO').length,
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-2" />
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            ))}
          </div>
          <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
        </div>
      </div>
    )
  }

  if (!currentScan) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-semibold mb-2">Scan Not Found</h2>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          The requested scan could not be found or you don't have access to it.
        </p>
        <Button asChild>
          <Link to="/history">Back to History</Link>
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/history">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-3">
              {getStatusIcon(currentScan.status)}
              {formatRepositoryName(currentScan.repository_url)}
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Scanned {formatRelativeTime(currentScan.created_at)} • {currentScan.branch} branch
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <ExportMenu
            scanId={scanId!}
            isCompleted={currentScan.status === 'completed'}
            selectedSeverities={selectedSeverity !== 'ALL' ? [selectedSeverity] : []}
            onExportStart={handleExportStart}
            onExportComplete={handleExportComplete}
            variant="dropdown"
          />
          <Button
            variant="outline"
            onClick={() => window.location.reload()}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Progress Bar (for running scans) */}
      {progress && currentScan.status === 'running' && (
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Scan Progress</span>
                <span>{progress.progress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress.progress}%` }}
                />
              </div>
              {progress.message && (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {progress.message}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Error Banner (for failed scans) */}
      {currentScan.status === 'failed' && (
        <Card className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <XCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-red-800 dark:text-red-200">
                  Scan Failed
                </h3>
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                  {currentScan.error_message || currentScan.error || 'The security scan could not be completed. Please try again.'}
                </p>
                {currentScan.failed_scanners && currentScan.failed_scanners.length > 0 && (
                  <div className="mt-3">
                    <p className="text-sm font-medium text-red-800 dark:text-red-200 mb-2">
                      Failed Scanners:
                    </p>
                    <ul className="list-disc list-inside text-sm text-red-700 dark:text-red-300 space-y-1">
                      {currentScan.failed_scanners.map((scanner: FailedScanner, idx: number) => (
                        <li key={idx}>
                          <span className="font-medium">{scanner.name}:</span> {scanner.error}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="mt-4">
                  <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Retry Scan
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Warning Banner (for scans with partial failures) */}
      {currentScan.status === 'completed_with_warnings' && (
        <Card className="border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-900/20">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <AlertTriangle className="h-6 w-6 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-yellow-800 dark:text-yellow-200">
                  Scan Completed with Warnings
                </h3>
                <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                  Some scanners failed but results are still available from the successful ones.
                </p>
                {currentScan.warnings && currentScan.warnings.length > 0 && (
                  <div className="mt-3">
                    <ul className="list-disc list-inside text-sm text-yellow-700 dark:text-yellow-300 space-y-1">
                      {currentScan.warnings.map((warning: string, idx: number) => (
                        <li key={idx}>{warning}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {currentScan.failed_scanners && currentScan.failed_scanners.length > 0 && (
                  <div className="mt-3">
                    <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-2">
                      Failed Scanners:
                    </p>
                    <ul className="list-disc list-inside text-sm text-yellow-700 dark:text-yellow-300 space-y-1">
                      {currentScan.failed_scanners.map((scanner: FailedScanner, idx: number) => (
                        <li key={idx}>
                          <span className="font-medium">{scanner.name}:</span> {scanner.error}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Findings */}
        <div className="lg:col-span-2 space-y-6">
          {/* Filters */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <Filter className="h-5 w-5" />
                Findings ({filteredFindings.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Input
                  placeholder="Search findings..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                
                <select
                  value={selectedSeverity}
                  onChange={(e) => setSelectedSeverity(e.target.value as SeverityLevel | 'ALL')}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="ALL">All Severities</option>
                  <option value="CRITICAL">Critical ({severityCount.CRITICAL})</option>
                  <option value="HIGH">High ({severityCount.HIGH})</option>
                  <option value="MEDIUM">Medium ({severityCount.MEDIUM})</option>
                  <option value="LOW">Low ({severityCount.LOW})</option>
                  <option value="INFO">Info ({severityCount.INFO})</option>
                </select>
                
                <select
                  value={selectedScanner}
                  onChange={(e) => setSelectedScanner(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="ALL">All Scanners</option>
                  {uniqueScanners.map(scanner => (
                    <option key={scanner} value={scanner}>
                      {scanner.charAt(0).toUpperCase() + scanner.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={showDismissed}
                    onChange={(e) => setShowDismissed(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  Show dismissed findings
                </label>
              </div>
            </CardContent>
          </Card>

          {/* Findings List */}
          {filteredFindings.length === 0 ? (
            <Card>
              <CardContent className="pt-6 text-center py-12">
                <Shield className="h-12 w-12 text-green-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">
                  {findings.length === 0 ? 'No Issues Found' : 'No Matching Findings'}
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  {findings.length === 0 
                    ? 'Great! No security issues were detected in this scan.'
                    : 'Try adjusting your filters to see more results.'
                  }
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredFindings.map((finding) => (
                <Card key={finding.id} className="relative">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <Badge
                          variant="outline"
                          className={getSeverityBadgeColor(finding.severity)}
                        >
                          {finding.severity}
                        </Badge>
                        <div className="flex-1 min-w-0">
                          <CardTitle className="text-base leading-6">
                            {finding.title}
                          </CardTitle>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="secondary" className="text-xs">
                              {finding.scanner}
                            </Badge>
                            {finding.cve_id && (
                              <Badge variant="outline" className="text-xs">
                                {finding.cve_id}
                              </Badge>
                            )}
                            {finding.cwe_id && (
                              <Badge variant="outline" className="text-xs">
                                CWE-{finding.cwe_id}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-1">
                        {dismissedFindings.has(finding.id) ? (
                          <Badge variant="secondary" className="text-xs">
                            Dismissed
                          </Badge>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDismissFinding(finding.id)}
                          >
                            <EyeOff className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  
                  <CardContent>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      {finding.description}
                    </p>
                    
                    {(finding.file_path || finding.package_name) && (
                      <div className="space-y-2 text-sm">
                        {finding.file_path && (
                          <div className="flex items-center gap-2">
                            <span className="font-medium">File:</span>
                            <code className="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded text-xs">
                              {finding.file_path}
                              {finding.line_number && `:${finding.line_number}`}
                            </code>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => copyToClipboard(finding.file_path || '')}
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                        )}
                        
                        {finding.package_name && (
                          <div className="flex items-center gap-2">
                            <span className="font-medium">Package:</span>
                            <code className="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded text-xs">
                              {finding.package_name}
                              {finding.package_version && `@${finding.package_version}`}
                            </code>
                            {finding.fixed_version && (
                              <Badge variant="success" className="text-xs">
                                Fix: {finding.fixed_version}
                              </Badge>
                            )}
                          </div>
                        )}

                        {finding.reference && (
                          <div className="flex items-center gap-2">
                            <span className="font-medium">Reference:</span>
                            <a
                              href={finding.reference}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-700 dark:text-blue-400 flex items-center gap-1 text-xs"
                            >
                              View Details <ExternalLink className="h-3 w-3" />
                            </a>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Right Column - Summary */}
        <div className="space-y-6">
          {/* Risk Score */}
          {currentScan.summary && (
            <RiskScoreCard
              score={currentScan.summary.risk_score}
              totalFindings={currentScan.summary.total_findings}
              criticalCount={currentScan.summary.critical_count}
              highCount={currentScan.summary.high_count}
            />
          )}

          {/* Scan Details */}
          <Card>
            <CardHeader>
              <CardTitle>Scan Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="font-medium">Status:</span>
                  <Badge
                    variant={
                      currentScan.status === 'completed' ? 'success' :
                      currentScan.status === 'completed_with_warnings' ? 'warning' :
                      currentScan.status === 'failed' ? 'destructive' :
                      currentScan.status === 'running' ? 'warning' :
                      'secondary'
                    }
                    className="ml-2 text-xs"
                  >
                    {getStatusLabel(currentScan.status)}
                  </Badge>
                </div>
                
                <div>
                  <span className="font-medium">Started:</span>
                  <div className="text-gray-600 dark:text-gray-400">
                    {formatDate(currentScan.created_at)}
                  </div>
                </div>
                
                {currentScan.completed_at && (
                  <>
                    <div>
                      <span className="font-medium">Completed:</span>
                      <div className="text-gray-600 dark:text-gray-400">
                        {formatDate(currentScan.completed_at)}
                      </div>
                    </div>
                    
                    <div>
                      <span className="font-medium">Duration:</span>
                      <div className="text-gray-600 dark:text-gray-400">
                        {formatDuration(
                          (new Date(currentScan.completed_at).getTime() - 
                           new Date(currentScan.created_at).getTime()) / 1000
                        )}
                      </div>
                    </div>
                  </>
                )}
                
                <div className="col-span-2">
                  <span className="font-medium">Scanners:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {currentScan.summary?.scanners_used.map(scanner => (
                      <Badge key={scanner} variant="secondary" className="text-xs">
                        {scanner}
                      </Badge>
                    ))}
                  </div>
                </div>
                
                {currentScan.commit_hash && (
                  <div className="col-span-2">
                    <span className="font-medium">Commit:</span>
                    <code className="ml-2 bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded text-xs">
                      {currentScan.commit_hash.substring(0, 8)}
                    </code>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Severity Breakdown */}
          {currentScan.summary && (
            <Card>
              <CardHeader>
                <CardTitle>Severity Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {[
                    { label: 'Critical', count: currentScan.summary.critical_count, color: 'text-red-600' },
                    { label: 'High', count: currentScan.summary.high_count, color: 'text-orange-600' },
                    { label: 'Medium', count: currentScan.summary.medium_count, color: 'text-yellow-600' },
                    { label: 'Low', count: currentScan.summary.low_count, color: 'text-blue-600' },
                    { label: 'Info', count: currentScan.summary.info_count, color: 'text-gray-600' },
                  ].map(item => (
                    <div key={item.label} className="flex items-center justify-between">
                      <span className={cn('font-medium', item.color)}>
                        {item.label}
                      </span>
                      <Badge variant="outline" className="text-xs">
                        {item.count}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}